import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OfficeLocation = ({ currentLanguage, translations }) => {
  const [activeTab, setActiveTab] = useState('map');

  const officeInfo = {
    address: {
      street: "Dietzweg 15",
      city: "22119 Hamburg",
      country: "Germany"
    },
    coordinates: {
      lat: 53.5511,
      lng: 9.9937
    },
    hours: [
      { day: translations?.office?.hours?.monday, time: "09:00 - 18:00" },
      { day: translations?.office?.hours?.tuesday, time: "09:00 - 18:00" },
      { day: translations?.office?.hours?.wednesday, time: "09:00 - 18:00" },
      { day: translations?.office?.hours?.thursday, time: "09:00 - 18:00" },
      { day: translations?.office?.hours?.friday, time: "09:00 - 17:00" },
      { day: translations?.office?.hours?.saturday, time: translations?.office?.hours?.closed },
      { day: translations?.office?.hours?.sunday, time: translations?.office?.hours?.closed }
    ],
    contact: {
      phone: "+49 172 7999621",
      email: "info@rexfel.com"
    },
    transportation: [
      {
        type: "U-Bahn",
        line: "U3",
        station: "Rathaus",
        duration: "2 min walk",
        icon: "Train"
      },
      {
        type: "S-Bahn", 
        line: "S1, S3",
        station: "Stadthausbrücke",
        duration: "5 min walk",
        icon: "Train"
      },
      {
        type: "Bus",
        line: "4, 5, 6",
        station: "Rödingsmarkt",
        duration: "3 min walk", 
        icon: "Bus"
      },
      {
        type: "Parking",
        line: translations?.office?.transportation?.parking,
        station: "Neuer Wall Garage",
        duration: "1 min walk",
        icon: "Car"
      }
    ]
  };

  const handleGetDirections = () => {
    const address = encodeURIComponent(`${officeInfo?.address?.street}, ${officeInfo?.address?.city}, ${officeInfo?.address?.country}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {translations?.office?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {translations?.office?.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map and Tabs */}
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('map')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'map' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="MapPin" size={16} className="inline mr-2" />
                {translations?.office?.tabs?.map}
              </button>
              <button
                onClick={() => setActiveTab('directions')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'directions' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="Navigation" size={16} className="inline mr-2" />
                {translations?.office?.tabs?.directions}
              </button>
            </div>

            {/* Map View */}
            {activeTab === 'map' && (
              <div className="bg-gray-100 rounded-2xl overflow-hidden h-96">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="REXFEL Office Location - Hamburg"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${officeInfo?.coordinates?.lat},${officeInfo?.coordinates?.lng}&z=16&output=embed`}
                  className="border-0"
                />
              </div>
            )}

            {/* Directions View */}
            {activeTab === 'directions' && (
              <div className="bg-gray-50 rounded-2xl p-6 h-96 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {translations?.office?.transportation?.title}
                </h3>
                <div className="space-y-4">
                  {officeInfo?.transportation?.map((transport, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 bg-white rounded-lg">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Icon name={transport?.icon} size={20} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{transport?.type}</span>
                          <span className="text-blue-600 font-medium">{transport?.line}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {transport?.station} • {transport?.duration}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  variant="default"
                  fullWidth
                  iconName="Navigation"
                  iconPosition="left"
                  onClick={handleGetDirections}
                  className="mt-6"
                >
                  {translations?.office?.actions?.getDirections}
                </Button>
              </div>
            )}
          </div>

          {/* Office Information */}
          <div className="space-y-8">
            {/* Address */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <Icon name="MapPin" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {translations?.office?.address?.title}
                  </h3>
                  <div className="text-gray-700 space-y-1">
                    <p className="font-medium">REXFEL UG</p>
                    <p>{officeInfo?.address?.street}</p>
                    <p>{officeInfo?.address?.city}</p>
                    <p>{officeInfo?.address?.country}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Icon name="Phone" size={20} className="mr-2" />
                Contact Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Main office</p>
                    <a href={`tel:${officeInfo?.contact?.phone}`} className="text-blue-600 hover:text-blue-700 font-medium">
                      {officeInfo?.contact?.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">General Inquiries</p>
                    <a href={`mailto:${officeInfo?.contact?.email}`} className="text-blue-600 hover:text-blue-700 font-medium">
                      {officeInfo?.contact?.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeLocation;